import axios from 'axios';

import type { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { readStatusCode } from '@/utils/apiErrors';

export type CheckoutErrorType = 'timeout' | 'network' | 'payment' | 'server';

// Intentionally coupled to the ChooseTimeScreen translation namespace: all
// checkout error keys (error.timeoutTitle, etc.) live under that prefix.
// Reusing the hook's return type keeps the keys type-checked against the
// strongly-typed i18n resources. A caller from another namespace would get a
// TS error — by design, since the keys would not exist there.
type ChooseTimeTFunction = ReturnType<
  typeof useScreenTranslations<'ChooseTimeScreen'>
>['t'];

const TIMEOUT_STATUS = new Set([408, 504]);
const TIMEOUT_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT']);

const isDeviceOffline = (): boolean => {
  // navigator.onLine is only reliable on web. When unavailable (native),
  // treat the device as online so no-response errors fall through to `timeout`,
  // which surfaces the safer "check your appointments" warning.
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.onLine !== 'boolean'
  ) {
    return false;
  }
  return navigator.onLine === false;
};

const classifyNoResponse = (code?: string): CheckoutErrorType => {
  if (code && TIMEOUT_CODES.has(code)) {
    return 'timeout';
  }
  return isDeviceOffline() ? 'network' : 'timeout';
};

/**
 * The api client rejects an HTTP error with the response body (restify's
 * `{ code, message }`, plus `statusCode` from api-client 1.2.3), and a request
 * with no response with `undefined` (an axios error from 1.2.3), so those
 * shapes are classified alongside a raw `AxiosError`.
 */
export const classifyCheckoutError = (error: unknown): CheckoutErrorType => {
  if (error === undefined) {
    return classifyNoResponse();
  }

  if (!axios.isAxiosError(error)) {
    const { code } = (error ?? {}) as { code?: unknown };
    const status = readStatusCode(error);
    if (status === 402 || code === 'PaymentRequired') return 'payment';
    if (status && TIMEOUT_STATUS.has(status)) return 'timeout';
    return 'server';
  }

  const status = error.response?.status;

  if (status === 402) {
    return 'payment';
  }

  if (status && TIMEOUT_STATUS.has(status)) {
    return 'timeout';
  }

  // No HTTP response: a timeout or a dropped/absent connection.
  if (!error.response) {
    return classifyNoResponse(error.code);
  }

  // Any other HTTP response (400, 409, 5xx, ...) is a generic server error.
  return 'server';
};

const CONTENT_KEYS = {
  timeout: { title: 'error.timeoutTitle', description: 'error.timeoutMessage' },
  network: { title: 'error.networkTitle', description: 'error.networkMessage' },
  payment: {
    title: 'error.paymentFailedTitle',
    description: 'error.paymentFailedMessage',
  },
  server: {
    title: 'error.serverErrorTitle',
    description: 'error.serverErrorMessage',
  },
} as const satisfies Record<
  CheckoutErrorType,
  { title: string; description: string }
>;

export const getCheckoutErrorContent = (
  type: CheckoutErrorType,
  t: ChooseTimeTFunction,
): { title: string; description: string } => {
  const keys = CONTENT_KEYS[type];
  return {
    title: t(keys.title),
    description: t(keys.description),
  };
};
